import {
  RemixiconComponentType,
  RiApps2AiLine,
  RiChat1Line,
  RiFileLine,
  RiFilterLine,
  RiFingerprintLine,
  RiFolderLine,
  RiInboxArchiveLine,
  RiPulseLine,
  RiSparklingLine,
  RiVideoLine,
} from '@remixicon/react';

import { isResourceName } from '../utils/names';
import {
  IconBusiness,
  IconChartBar,
  IconChartLine,
  IconEvents,
  IconFlag,
  IconGridView,
  IconLab,
  IconStations,
  IconUser,
  RolloutIcon,
} from './Icons';

export type Module =
  | 'metrics'
  | 'abtests'
  | 'rollouts'
  | 'flags'
  | 'segments'
  | 'events'
  | 'analysis'
  | 'insights'
  | 'explore'
  | 'surfaces'
  | 'initiatives'
  | 'issues'
  | 'collections'
  | 'projects'
  | 'entities'
  | 'pulses'
  | 'chats'
  | 'recordings';

export const ModuleIcons: Record<Module, RemixiconComponentType> = {
  metrics: IconChartLine,
  flags: IconFlag,
  abtests: IconLab,
  rollouts: RolloutIcon,
  events: IconEvents,
  analysis: IconChartBar,
  insights: IconStations,
  explore: RiSparklingLine,
  segments: IconUser,
  surfaces: IconGridView,
  initiatives: IconBusiness,
  issues: RiInboxArchiveLine,
  collections: RiFolderLine,
  projects: RiApps2AiLine,
  entities: RiFingerprintLine,
  pulses: RiPulseLine,
  chats: RiChat1Line,
  recordings: RiVideoLine,
};

export interface ModuleStyle {
  Icon: RemixiconComponentType;
  color: string;
  label: string;
}

export const MODULE_STYLES: Record<Module, ModuleStyle> = {
  metrics: { Icon: ModuleIcons.metrics, color: '#06B6D4', label: 'Metric' },
  abtests: { Icon: ModuleIcons.abtests, color: '#8B5CF6', label: 'A/B test' },
  rollouts: { Icon: ModuleIcons.rollouts, color: '#F59E0B', label: 'Rollout' },
  flags: { Icon: ModuleIcons.flags, color: '#E74C3C', label: 'Flag' },
  segments: { Icon: ModuleIcons.segments, color: '#EC4899', label: 'Segment' },
  events: { Icon: ModuleIcons.events, color: '#10B981', label: 'Event' },
  analysis: { Icon: ModuleIcons.analysis, color: '#3B82F6', label: 'Analysis' },
  insights: { Icon: ModuleIcons.insights, color: '#A855F7', label: 'Insight' },
  explore: { Icon: ModuleIcons.explore, color: '#FACC15', label: 'Explore' },
  surfaces: { Icon: ModuleIcons.surfaces, color: '#0EA5E9', label: 'Surface' },
  initiatives: {
    Icon: ModuleIcons.initiatives,
    color: '#64748B',
    label: 'Initiative',
  },
  issues: {
    Icon: ModuleIcons.issues,
    color: '#0F766E',
    label: 'Issue',
  },
  collections: {
    Icon: ModuleIcons.collections,
    color: '#94A3B8',
    label: 'Collection',
  },
  projects: { Icon: ModuleIcons.projects, color: '#6366F1', label: 'Project' },
  entities: { Icon: ModuleIcons.entities, color: '#78716C', label: 'Entity' },
  pulses: {
    Icon: ModuleIcons.pulses,
    color: 'var(--text-bright-accent)',
    label: 'Pulse',
  },
  chats: { Icon: ModuleIcons.chats, color: '#6366F1', label: 'Chat' },
  recordings: {
    Icon: ModuleIcons.recordings,
    color: '#A855F7',
    label: 'Recording',
  },
};

const DOCUMENT_STYLE: ModuleStyle = {
  Icon: RiFileLine,
  color: '#6366F1',
  label: 'Document',
};

const RULE_STYLE: ModuleStyle = {
  Icon: RiFilterLine,
  color: '#0D9488',
  label: 'Rule',
};

const stripPrefix = (path: string): string =>
  path
    .replace(/^\/\//, '')
    .replace(/^\/confidence(?=\/)/, '')
    .replace(/^\//, '');

export const pathToModuleStyle = (path: string): ModuleStyle | undefined => {
  const normalized = stripPrefix(path);

  if (isResourceName(normalized, 'workflows/abtest'))
    return MODULE_STYLES.abtests;
  if (isResourceName(normalized, 'workflows/rollout'))
    return MODULE_STYLES.rollouts;
  if (
    isResourceName(normalized, 'workflows/analysis') ||
    isResourceName(normalized, 'workflows/reanalysis')
  ) {
    return MODULE_STYLES.analysis;
  }
  if (isResourceName(normalized, 'rules') || normalized.includes('/rules/')) {
    return RULE_STYLE;
  }
  if (isResourceName(normalized, 'flags')) return MODULE_STYLES.flags;
  if (isResourceName(normalized, 'events')) return MODULE_STYLES.events;
  if (isResourceName(normalized, 'metrics')) return MODULE_STYLES.metrics;
  if (isResourceName(normalized, 'issues')) return MODULE_STYLES.issues;
  if (isResourceName(normalized, 'segments')) return MODULE_STYLES.segments;
  if (isResourceName(normalized, 'pulses')) return MODULE_STYLES.pulses;
  if (isResourceName(normalized, 'collaborativeDocuments')) {
    return DOCUMENT_STYLE;
  }
  if (isResourceName(normalized, 'externalDocuments')) {
    return DOCUMENT_STYLE;
  }

  return undefined;
};

export const workflowToIcon = (workflow: string): RemixiconComponentType => {
  switch (workflow) {
    default:
    case 'abest':
      return ModuleIcons.abtests;
    case 'rollout':
      return ModuleIcons.rollouts;
    case 'analysis':
      return ModuleIcons.analysis;
  }
};

const WORKFLOW_TYPE_STYLES: Record<string, ModuleStyle> = {
  abtest: MODULE_STYLES.abtests,
  rollout: MODULE_STYLES.rollouts,
  analysis: MODULE_STYLES.analysis,
  reanalysis: MODULE_STYLES.analysis,
};

const additionalWorkflowTypeStyles = new Map<string, ModuleStyle>();

export const registerWorkflowTypeStyles = (
  styles: Record<string, ModuleStyle>,
) => {
  for (const [key, style] of Object.entries(styles)) {
    additionalWorkflowTypeStyles.set(key, style);
  }
};

const RESOURCE_STYLES: Record<string, ModuleStyle> = {
  flags: MODULE_STYLES.flags,
  segments: MODULE_STYLES.segments,
  materializedSegments: MODULE_STYLES.segments,
  metrics: MODULE_STYLES.metrics,
  measurements: MODULE_STYLES.metrics,
  eventDefinitions: MODULE_STYLES.events,
  surfaces: MODULE_STYLES.surfaces,
  issues: MODULE_STYLES.issues,
  projects: MODULE_STYLES.projects,
  chats: MODULE_STYLES.chats,
  initiatives: MODULE_STYLES.initiatives,
  recordings: MODULE_STYLES.recordings,
  pulses: MODULE_STYLES.pulses,
  collections: MODULE_STYLES.collections,
  assignmentTables: MODULE_STYLES.entities,
  factTables: MODULE_STYLES.entities,
  entities: MODULE_STYLES.entities,
};

export const resourceNameToModuleStyle = (
  resourceName: string,
): ModuleStyle | undefined => {
  const parts = resourceName.split('/');

  if (parts[0] === 'workflows' && parts.length >= 2) {
    return (
      WORKFLOW_TYPE_STYLES[parts[1]] ??
      additionalWorkflowTypeStyles.get(parts[1])
    );
  }

  return RESOURCE_STYLES[parts[0]];
};
